import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { intakeService } from '@/services/intake.service';
import type {
  IntakeCandidate,
  IntakeCandidateChoice,
  IntakeFixSuggestion,
  IntakeFormData,
  IntakeTicketConfirmation,
  IntakeWizardStep,
} from '@/types/intake';

export interface UseIntakeWizardResult {
  step: IntakeWizardStep;
  candidates: IntakeCandidate[];
  fix: IntakeFixSuggestion | null;
  ticket: IntakeTicketConfirmation | null;
  error: boolean;
  isBusy: boolean;
  submit: (form: IntakeFormData) => void;
  chooseCandidate: (choice: IntakeCandidateChoice) => void;
  answerFix: (solved: boolean) => void;
  retry: () => void;
  reset: () => void;
}

/**
 * One-way wizard step machine driving the inline IntakePanel flow (D-12/D-15).
 * No step, once left, is ever re-entered for a given submission — guards are
 * keyed on the current `step` value rather than a separate "answered" flag.
 */
export function useIntakeWizard(): UseIntakeWizardResult {
  const [step, setStep] = useState<IntakeWizardStep>('form');
  const [candidates, setCandidates] = useState<IntakeCandidate[]>([]);
  const [fix, setFix] = useState<IntakeFixSuggestion | null>(null);
  const [ticket, setTicket] = useState<IntakeTicketConfirmation | null>(null);
  const [error, setError] = useState(false);

  // Refs, not state: these never drive a render on their own — they are
  // read by later actions (chooseCandidate/answerFix/retry) triggered by
  // the admin, not by React itself.
  const formRef = useRef<IntakeFormData | null>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const lastActionRef = useRef<(() => void) | null>(null);

  const checkMutation = useMutation({
    mutationFn: ({
      form,
      choice,
    }: {
      form: IntakeFormData;
      choice?: IntakeCandidateChoice;
    }) => intakeService.check(form, choice),
  });

  const escalateMutation = useMutation({
    mutationFn: ({ form, token }: { form: IntakeFormData; token?: string }) =>
      intakeService.escalate(form, token),
  });

  const runEscalate = useCallback(
    (form: IntakeFormData, token?: string) => {
      lastActionRef.current = () => runEscalate(form, token);
      setStep('escalation_loading');
      escalateMutation.mutate(
        { form, token },
        {
          onSuccess: (res) => {
            setError(false);
            setTicket(res.ticket);
            setStep('escalation_confirmation');
          },
          onError: () => setError(true),
        },
      );
    },
    [escalateMutation],
  );

  const runCheck = useCallback(
    (form: IntakeFormData, choice?: IntakeCandidateChoice) => {
      lastActionRef.current = () => runCheck(form, choice);
      setStep('diagnostic_loading');
      checkMutation.mutate(
        { form, choice },
        {
          onSuccess: (res) => {
            setError(false);
            if (res.step === 'candidate_confirm') {
              setCandidates(res.candidates);
              setStep('candidate_confirm');
              return;
            }
            if (res.step === 'fix_card') {
              tokenRef.current = res.escalationToken;
              setFix(res.fix);
              setStep('fix_card');
              return;
            }
            // res.step === 'no_fix' — FIXMATCH-04: auto-escalate immediately,
            // the fix card never renders.
            tokenRef.current = res.escalationToken;
            runEscalate(form, res.escalationToken);
          },
          onError: () => setError(true),
        },
      );
    },
    [checkMutation, runEscalate],
  );

  const submit = useCallback(
    (form: IntakeFormData) => {
      formRef.current = form;
      tokenRef.current = undefined;
      setCandidates([]);
      setFix(null);
      setTicket(null);
      setError(false);
      if (form.requestType !== 'issue') {
        // FIXMATCH-03: Feature request / Feedback skip fix-match entirely.
        runEscalate(form, undefined);
        return;
      }
      runCheck(form, undefined);
    },
    [runCheck, runEscalate],
  );

  const chooseCandidate = useCallback(
    (choice: IntakeCandidateChoice) => {
      if (step !== 'candidate_confirm') return;
      const form = formRef.current;
      if (!form) return;
      runCheck(form, choice);
    },
    [step, runCheck],
  );

  const answerFix = useCallback(
    (solved: boolean) => {
      // D-15: once left, 'fix_card' never re-enters — this guard alone makes
      // a second answerFix call a no-op, since step has already moved on.
      if (step !== 'fix_card') return;
      const form = formRef.current;
      if (!form) return;
      if (solved) {
        // D-14: no ticket is ever created for a resolved submission.
        setStep('resolved');
        return;
      }
      runEscalate(form, tokenRef.current);
    },
    [step, runEscalate],
  );

  const retry = useCallback(() => {
    lastActionRef.current?.();
  }, []);

  const reset = useCallback(() => {
    setStep('form');
    setCandidates([]);
    setFix(null);
    setTicket(null);
    setError(false);
    formRef.current = null;
    tokenRef.current = undefined;
    lastActionRef.current = null;
  }, []);

  return {
    step,
    candidates,
    fix,
    ticket,
    error,
    isBusy: checkMutation.isPending || escalateMutation.isPending,
    submit,
    chooseCandidate,
    answerFix,
    retry,
    reset,
  };
}
